import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { CheckCircle2, XCircle, ChevronRight, Eye, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending_approval', label: 'Pending Approval' },
  { key: 'approved', label: 'Approved' },
  { key: 'packed', label: 'Packed' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'dispatched', label: 'Dispatched' },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

const STATUS_BADGE = {
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-blue-100 text-blue-800 border-blue-300',
  ready_to_pick: 'bg-blue-100 text-blue-800 border-blue-300',
  pending_procurement: 'bg-blue-100 text-blue-800 border-blue-300',
  packed: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  invoiced: 'bg-violet-100 text-violet-800 border-violet-300',
  dispatched: 'bg-sky-100 text-sky-800 border-sky-300',
  out_for_delivery: 'bg-teal-100 text-teal-800 border-teal-300',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-rose-100 text-rose-800 border-rose-300',
};

export default function OGOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [detailOpen, setDetailOpen] = useState(null);
  const [retailerCredit, setRetailerCredit] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders?source=og_retailer_app`, { headers: authHeaders() });
      setOrders(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load OG orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const counts = STATUS_FILTERS.reduce((acc, f) => {
    acc[f.key] = f.key === 'all' ? orders.length : orders.filter(o => o.status === f.key).length;
    return acc;
  }, {});

  const onApprove = async (order) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${order.id}/approve`, {}, { headers: authHeaders() });
      toast.success(`Order ${order.order_number} approved`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approval failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    if (!cancelOpen) return;
    if (!cancelReason.trim()) { toast.warning('Please enter a reason'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${cancelOpen.id}/cancel`,
        { rejection_reason: cancelReason.trim() }, { headers: authHeaders() });
      toast.success(`Order ${cancelOpen.order_number} cancelled`);
      setCancelOpen(null); setCancelReason('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cancellation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onAdvance = async (order) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders/${order.id}/advance-status`, {}, { headers: authHeaders() });
      toast.success(`${order.order_number} → ${(res.data?.status || '').replace(/_/g, ' ')}`);
      await load();
      if (detailOpen?.id === order.id) {
        setDetailOpen(res.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Advance failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="og-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">OG Orders</h1>
          <p className="text-sm text-slate-500 mt-1">
            Orders placed via the OG Retailer mobile app — approve to enter the
            fulfilment pipeline (approved → packed → invoiced → dispatched →
            out for delivery → delivered).
          </p>
        </div>
        <Badge className="bg-orange-500 hover:bg-orange-500 text-white">FROM OG APP</Badge>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`og-orders-filter-${f.key}`}
            className={`px-3 py-1.5 text-sm rounded-full border transition ${
              filter === f.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
            }`}
          >
            {f.label} <span className="ml-1 opacity-70">{counts[f.key] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="text-center text-slate-500 py-12">
            No OG orders in this view.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Order #</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Retailer</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Phone</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Items</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Total</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Status</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => (
                  <tr key={o.id} className="border-b hover:bg-slate-50" data-testid={`og-order-row-${o.id}`}>
                    <td className="px-3 py-2 font-mono">{o.order_number}</td>
                    <td className="px-3 py-2">{o.franchise_name || o.business_name || '—'}</td>
                    <td className="px-3 py-2">{o.retailer_phone || '—'}</td>
                    <td className="px-3 py-2 text-right">{(o.items || []).length}</td>
                    <td className="px-3 py-2 text-right">₹{Number(o.total || 0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2">
                      <Badge className={`border text-xs ${STATUS_BADGE[o.status] || 'bg-slate-100 text-slate-700'}`}
                             data-testid={`og-order-status-${o.id}`}>
                        {(o.status || 'unknown').replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setDetailOpen(o)} data-testid={`og-order-view-${o.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        {o.status === 'pending_approval' && (
                          <>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => onApprove(o)} disabled={submitting}
                                    data-testid={`og-order-approve-${o.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline"
                                    className="text-rose-600 border-rose-300"
                                    onClick={() => setCancelOpen(o)} disabled={submitting}
                                    data-testid={`og-order-cancel-${o.id}`}>
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        {['approved', 'ready_to_pick', 'pending_procurement', 'packed', 'invoiced', 'dispatched', 'out_for_delivery'].includes(o.status) && (
                          <Button size="sm" variant="outline" onClick={() => onAdvance(o)} disabled={submitting}
                                  data-testid={`og-order-advance-${o.id}`}>
                            <ChevronRight className="w-3.5 h-3.5 mr-1" /> Advance
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      <Dialog open={!!detailOpen} onOpenChange={(o) => {
        if (!o) { setDetailOpen(null); setRetailerCredit(null); }
        else if (detailOpen?.external_customer_id || detailOpen?.franchise_id) {
          // Fetch the retailer's available credit so we can warn the admin.
          axios.get(`${API}/customers?source=og_retailer_app&status=active`, { headers: authHeaders() })
            .then(r => {
              const ext = detailOpen.external_customer_id || detailOpen.customer_external_id;
              const phone = detailOpen.retailer_phone;
              const match = (r.data || []).find(c => c.external_id === ext || c.franchise_id === detailOpen.franchise_id || c.phone === phone || c.mobile === phone);
              if (match) setRetailerCredit(match);
            })
            .catch(() => {});
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="og-order-detail-dialog">
          <DialogHeader>
            <DialogTitle>Order {detailOpen?.order_number}</DialogTitle>
          </DialogHeader>
          {detailOpen && (
            <div className="space-y-4 text-sm">
              {/* Retailer Credit panel */}
              {retailerCredit && (() => {
                const limit = Number(retailerCredit.credit_limit || 0);
                const outstanding = Number(retailerCredit.outstanding_amount || 0);
                const available = Math.max(0, limit - outstanding);
                const total = Number(detailOpen.total || 0);
                const isCredit = (detailOpen.payment_method || '').toLowerCase().includes('credit');
                const exceedsCredit = isCredit && total > available && detailOpen.status === 'pending_approval';
                return (
                  <div className={`border rounded p-3 ${exceedsCredit ? 'bg-rose-50 border-rose-300' : 'bg-emerald-50 border-emerald-200'}`}
                       data-testid="og-order-credit-panel">
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div><div className="text-slate-500">Limit</div><div className="font-semibold">₹{limit.toLocaleString('en-IN')}</div></div>
                      <div><div className="text-slate-500">Outstanding</div><div className="font-semibold">₹{outstanding.toLocaleString('en-IN')}</div></div>
                      <div><div className="text-slate-500">Available</div><div className="font-semibold text-emerald-700">₹{available.toLocaleString('en-IN')}</div></div>
                      <div><div className="text-slate-500">This Order</div><div className="font-semibold">₹{total.toLocaleString('en-IN')}</div></div>
                    </div>
                    {exceedsCredit && (
                      <div className="text-xs text-rose-700 font-medium mt-2">
                        ⚠️ Order total exceeds available credit by ₹{(total - available).toLocaleString('en-IN')} — confirm with retailer before approving.
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500">Retailer</div>
                  <div className="font-medium">{detailOpen.franchise_name || detailOpen.business_name || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Phone</div>
                  <div>{detailOpen.retailer_phone || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">GSTIN</div>
                  <div className="font-mono">{detailOpen.gstin || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Payment</div>
                  <div>{detailOpen.payment_method || '—'} <span className="text-xs text-slate-500">({detailOpen.payment_status || 'pending'})</span></div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-slate-500">Address</div>
                  <div className="text-slate-700">{detailOpen.address || detailOpen.delivery_address || '—'}</div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2 text-slate-800">Line Items</h4>
                <table className="w-full border text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Product</th>
                      <th className="px-2 py-1 text-right">Qty</th>
                      <th className="px-2 py-1 text-right">Unit ₹</th>
                      <th className="px-2 py-1 text-right">Total ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailOpen.items || []).map((it, idx) => (
                      <tr key={it._uid || idx} className="border-t">
                        <td className="px-2 py-1">{it.product_name}</td>
                        <td className="px-2 py-1 text-right">{it.quantity}</td>
                        <td className="px-2 py-1 text-right">{Number(it.unit_price || 0).toLocaleString('en-IN')}</td>
                        <td className="px-2 py-1 text-right">{Number(it.total_price || it.quantity * (it.unit_price || 0)).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-right text-xs text-slate-600 mt-2 space-y-0.5">
                  <div>Subtotal: ₹{Number(detailOpen.subtotal || 0).toLocaleString('en-IN')}</div>
                  <div>Tax: ₹{Number(detailOpen.tax || 0).toLocaleString('en-IN')}</div>
                  <div className="text-base font-bold text-slate-900">Total: ₹{Number(detailOpen.total || 0).toLocaleString('en-IN')}</div>
                </div>
              </div>

              {(detailOpen.status_timeline || []).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-slate-800">Timeline</h4>
                  <div className="space-y-1 text-xs">
                    {detailOpen.status_timeline.map((t, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-slate-400 w-44">{new Date(t.at).toLocaleString()}</span>
                        <span className="capitalize">{(t.from || '').replace(/_/g, ' ')}</span>
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                        <span className="capitalize font-medium">{(t.to || '').replace(/_/g, ' ')}</span>
                        <span className="text-slate-400">— {t.by_name || t.by}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailOpen.notes && (
                <div>
                  <div className="text-xs text-slate-500">Notes</div>
                  <div className="text-slate-700">{detailOpen.notes}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {detailOpen && ['approved', 'ready_to_pick', 'pending_procurement', 'packed', 'invoiced', 'dispatched', 'out_for_delivery'].includes(detailOpen.status) && (
              <Button onClick={() => onAdvance(detailOpen)} disabled={submitting} data-testid="og-detail-advance-btn">
                <ChevronRight className="w-4 h-4 mr-1" /> Advance Status
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={!!cancelOpen} onOpenChange={(o) => !o && setCancelOpen(null)}>
        <DialogContent data-testid="og-order-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel {cancelOpen?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Cancellation Reason</label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Out of stock, duplicate order, payment failure"
              rows={3}
              data-testid="og-order-cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(null)} disabled={submitting}>Cancel</Button>
            <Button variant="destructive" onClick={onCancel} disabled={submitting || !cancelReason.trim()}
                    data-testid="og-order-cancel-confirm-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
